import  moment from 'moment';
import { UserModel } from '../models/user.model';
import { Camera } from '@awesome-cordova-plugins/camera/ngx';
import { of, ReplaySubject, Subject, firstValueFrom } from 'rxjs';
import { Component, NgModule, OnDestroy } from '@angular/core';
import { switchMap, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../services/user.service';
import { AuthService } from '../services/auth.service';
import { maxImages } from '../constants/general.constant';
import { MediaCaptureProvider } from '../services/media-capture';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { LoadingController, ToastController, Platform, ActionSheetController } from '@ionic/angular';

@Component({
    template: ''
})

export class BasePage implements OnDestroy {
    protected readonly destroy = new Subject<boolean>();
    protected readonly user$ = new ReplaySubject<UserModel>(1);

    user: UserModel;
    loggedIn = true;
    cordova = false;
    isDetail = false;
    imagesArray = [];
    isLoading = false;
    activeLoader: HTMLIonLoadingElement | null = null;
    selectedImages = [];
    timeFormat = 'HH:mm:ss';
    dateFormat = 'YYYY-MM-DD';
    dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';

    constructor(
        protected platform: Platform,
        protected userService: UserService,
        protected authService: AuthService,
        protected translate: TranslateService,
        protected loadingCtrl: LoadingController,
        private toastController?: ToastController,
        public mediaCapture?: MediaCaptureProvider,
        public actionSheetCtrl?: ActionSheetController,
        public camera?: Camera
    ) {
        authService.onLoginChange
            .pipe(
                switchMap(loggedIn => {
                    this.loggedIn = loggedIn;

                    const url = window.location.pathname;
                    if (loggedIn
                        && url !== '/register'
                        && url !== '/login'
                        && url !== '/forgot'
                        && url !== '/verify-email'
                        && url !== '/reset-password') {
                        return userService.get();
                    }

                    return of<UserModel>(null);
                }),
                takeUntil(this.destroy)
            )
            .subscribe(user => {
                this.user = user;
                this.user$.next(user);
                this.userService.updatedUser.next(user);
            });
        if (this.platform.is('hybrid')) {
            this.cordova = true;
        }
    }

    protected convertToFormData(value: any, result?: FormData, key?: string) {
        result = result || new FormData();

        if (typeof value === 'function') {
            throw new Error('Cannot serialize functions');
        }

        if (
            (value === null || typeof value !== 'object' || Array.isArray(value)) &&
            !key
        ) {
            throw new Error(
                `Cannot serialize ${Array.isArray(value) ? 'array' : typeof value
                } values as root element`
            );
        }

        if (typeof value === 'undefined') {
            return result;
        }

        if (value === null) {
            result.append(key, '');
            return result;
        }

        if (typeof value === 'boolean') {
            result.append(key, value ? '1' : '0');
            return result;
        }

        if (Array.isArray(value)) {
            value.forEach((item: any, index: number) =>
                this.convertToFormData(item, result, `${key}[${index}]`)
            );
            return result;
        }

        if (typeof value === 'object') {
            if (value instanceof Date) {
                result.append(key, moment(value).toISOString());
                return result;
            }

            if (value instanceof Blob) {
                result.append(key, value, (value as File).name);
                return result;
            }

            // TODO maybe handle more types??

            Object.keys(value).forEach(item => {
                if (key) {
                    this.convertToFormData(value[item], result, `${key}[${item}]`);
                } else {
                    this.convertToFormData(value[item], result, item);
                }
            });
            return result;
        }

        result.append(key, `${value}`);

        return result;
    }

    protected touchControls(control: AbstractControl) {
        control.markAsTouched();

        if (control instanceof UntypedFormArray) {
            control.controls.forEach(child => this.touchControls(child));
        } else if (control instanceof UntypedFormGroup) {
            Object.keys(control.controls).forEach(name =>
                this.touchControls(control.controls[name])
            );
        }
    }

    async showLoader() {
        this.isLoading = true;
        try {
            const messages = await firstValueFrom(this.translate.get(['Loading']));
            this.activeLoader = await this.loadingCtrl.create({
                message: messages['Loading']
            });
            await this.activeLoader.present();
            if (!this.isLoading) {
                // hideLoader() werd al aangeroepen terwijl we nog aan het opbouwen waren
                await this.hideLoader();
            }
        } catch (error) {
            console.error('showLoader failed', error);
        }
    }

    async hideLoader() {
        this.isLoading = false;
        if (this.activeLoader) {
            const loader = this.activeLoader;
            this.activeLoader = null;
            try {
                await loader.dismiss();
            } catch (error) {
                console.warn('hideLoader dismiss failed', error);
            }
        }
    }
    
    async showToast(message) {
        const doneText = this.translate.instant('Buttons.Done'); // translate button
        const toast = await this.toastController.create({
            message,
            duration: 5000,
            position: 'top',
            buttons: [
                {
                    text: doneText,
                    role: 'cancel',
                    handler: () => {
                        console.log('Cancel clicked');
                    }
                }
            ]
        });
        toast.present();
    }

    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.complete();
    }

    getTime(post: any) {
        for (const item of post) {
            item.time_by = moment(item.created_at).fromNow();
        }
        return post;
    }

    /**
     * Takes object and reutrns object with out null values
     *
     * @param obj any, object from which null keys will be removed
     * @returns any, return object without null keys
     */
    clean(obj: any): any {
        for (const propName in obj) {
            if (obj[propName] === null || obj[propName] === undefined) {
                delete obj[propName];
            }
        }
        return obj;
    }

    /**
     * It will format the date string and returns the string
     *
     * @param dateTime String of datetime
     * @param format String in which datetime needs to be converted
     * @string returns String of formatted type
     */
    returnFormatedDateTime(dateTime: string, format: string): string {
        return moment(dateTime).format(format);
    }

    cordovaPicture(event: Event, maxUpload = maxImages) {
        if ((window as any).cordova && (window as any).cordova.platformId !== 'browser') {
            event.stopPropagation();
            this.translate.get([
                'Camera.Source.Camera',
                'Camera.Source.PhotoLibrary',
                'ValidationMessage.ValidImage',
                'ValidationMessage.NoFileSelected',
                'Buttons.Cancel',
                'ValidationMessage.MaxUploadLimit'
            ]).subscribe(async messages => {
                if (this.selectedImages && this.selectedImages.length >= maxUpload) {
                    this.showToast(messages['ValidationMessage.MaxUploadLimit']);
                    return;
                } else {
                    const alert = await this.actionSheetCtrl.create({
                        header: messages['Camera.Select'],
                        backdropDismiss: true,
                        buttons: [
                            {
                                icon: 'close',
                                text: messages['Buttons.Cancel'],
                                role: 'cancel'
                            },
                            {
                                icon: 'camera',
                                text: messages['Camera.Source.Camera'],
                                handler: () => {
                                    this.mediaCapture.getPicture(this.camera.PictureSourceType.CAMERA,
                                        true, this.camera.MediaType.PICTURE).then(
                                            async (result: any) => {
                                                this.showLoader();
                                                if (result) {
                                                    if (result.file.indexOf('data:image/') >= 0) {
                                                        // todo write code, what you wana do with image
                                                        this.imagesArray = [];
                                                        this.imagesArray.push(result.file);
                                                    } else {
                                                        this.showToast(messages['ValidationMessage.ValidImage']);
                                                    }
                                                }
                                                this.hideLoader();
                                            },
                                            error => {
                                                this.hideLoader();
                                                this.showToast(error || messages['ValidationMessage.NoFileSelected']);
                                            }
                                        );
                                }
                            },
                            {
                                icon: 'images-outline',
                                text: messages['Camera.Source.PhotoLibrary'],
                                handler: () => {
                                    this.mediaCapture.getPicture(this.camera.PictureSourceType.PHOTOLIBRARY,
                                        true, this.camera.MediaType.PICTURE).then(
                                            async (result: any) => {
                                                this.showLoader();
                                                if (result) {
                                                    if (result.file.indexOf('data:image/') >= 0) {
                                                        // todo write code, what you wana do with image
                                                        this.imagesArray = [];
                                                        this.imagesArray.push(result.file);
                                                    } else {
                                                        this.showToast(messages['ValidationMessage.ImageOnly']);
                                                    }
                                                }
                                                this.hideLoader();
                                            },
                                            error => {
                                                this.hideLoader();
                                                this.showToast(error || messages['ValidationMessage.NoFileSelected']);
                                            }
                                        );
                                }
                            }
                        ]
                    });
                    await alert.present();
                }
            });
        }
    }

    getImages(event: UIEvent, maxUpload = maxImages) {
        this.translate.get([
            'ValidationMessage.MaxUploadLimit',
            'ValidationMessage.ValidImage'
        ]).subscribe(messages => {
            if (this.selectedImages && this.selectedImages.length >= maxUpload) {
                this.showToast(messages['ValidationMessage.MaxUploadLimit']);
                return;
            } else {
                const target = event.target as HTMLInputElement;
                const imageFiles = target.files;
                Array.from(imageFiles).forEach(element => {
                    console.log(element);
                    this.mediaCapture.readFile(element).then((base64data) => {
                        if (base64data.indexOf('data:image/') >= 0) {
                            this.selectedImages.push({ file: base64data, name: element.name });
                            this.imagesArray.push(base64data);
                        } else {
                            this.showToast(messages['ValidationMessage.ValidImage']);
                        }
                    });
                });
            }
        });
    }

    /**
     * Convert Base64 to blob
     *
     * @param item object of {file: string; name: string}
     * @returns Blob
     */
    getBlobOfImage(item: { file: string; name: string }): Blob {
        let contentType = 'image/jpg';
        // Split the base64 string in data and contentType
        const block = item.file.split(';');
        // Get the content type
        const dataType = block[0].split(':')[1]; // In this case "image/png"
        if (dataType) {
            contentType = dataType;
        }
        let realData = '';
        // get the real base64 content of the file
        if (block[1] === 'charset=utf-8') {
            realData = block[2].split(',')[1]; // In this case "iVBORw0KGg...."
        } else {
            realData = block[1].split(',')[1]; // In this case "iVBORw0KGg...."
        }
        return this.b64toBlob(realData, contentType);
    }

    /**
     * Convert a base64 string in a Blob according to the data and contentType.
     *
     * @param b64Data type:String Pure base64 string without contentType
     * @param contentType type:String the content type of the file i.e (image/jpeg - image/png - text/plain)
     * @param sliceSize type:Int SliceSize to process the byteCharacters
     * @return Blob
     */

    b64toBlob(b64Data: string, contentType: string, sliceSize: number = 512): Blob {
        contentType = contentType || '';
        sliceSize = sliceSize || 512;
        if (b64Data) {
            b64Data = b64Data.replace(/ /g, '');
        }
        const byteCharacters = atob(b64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    }

    public clearMediaInput(event: any): void {
        event.target.value = null;
    }

    /**
     * checking if filters filters are applied
     * @param params contains query params for listings
     * @returns true if filters applied otherwise false
     */
    isFilterApplied(params, type?: string) {
        let isFiltered = false;
        let defaultParams = [
            'category_id', 'condition_id', 'search', 'brand_id', 'type_id', 'color_id', 'price_from', 'price_to', 'location', 'latitude', 'longitude', 'user_id', 'other', 'size_id'];

        if (type === 'category') {
            defaultParams = defaultParams.filter(key => key !== 'category_id');
        }

        if (type === 'search') {
            defaultParams = defaultParams.filter(key => key !== 'search');
        }

        // checking if filters are applied or not
        for (const param of Object.keys(params)) {
            const result = defaultParams.indexOf(param);
            if (result !== -1) {
                isFiltered = true;
                break;
            }
        }
        return isFiltered;
    }

    // async showImageActionSheet(image?: any) {
    //   const sheet = await  this.actionSheetCtrl.create({
    //     header: 'Profile Image',
    //     backdropDismiss: true,
    //     buttons: [
    //       {
    //         icon: 'eye',
    //         text: "Preview",
    //         role: 'show-img',
    //         handler: () => {

    //           if(!this.cordova) {
    //             this.showToast('Preview is Available only on Mobile Devices');
    //             return;
    //           }

    //           if(!image) {
    //             return;
    //           }

    //           this.photoviewer.show(image, '', {
    //               share: false, // default is false
    //               closeButton: false, // default is true
    //               copyToReference: false, // default is false
    //               headers: '',  // If this is not provided, an exception will be triggered
    //               piccasoOptions: {} // If this is not provided, an exception will be triggered
    //           });
              
    //         }
    //       }
    //     ]
    //   });

    //   await sheet.present();
    // }
}


